// src/firebase.js
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getDatabase, ref, set } from "firebase/database";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";

// —— All app data is stored in this Firebase project ——
// • Realtime Database (managementDb): home, about, services, gallery, contacts, join-us
// • Firestore (db): expense_projects, expense_clients, expense_expenses, expense_invoices, expense_settings, expense_invoice_counters
// • Storage (storage): invoices/*.pdf
// • Auth (auth): dashboard login
const managementFirebaseConfig = {
  apiKey: "AIzaSyDQzviSbexu8A7taQOi5R9Hu8HxfmETtfI",
  authDomain: "thevideocompanysitedb.firebaseapp.com",
  databaseURL: "https://thevideocompanysitedb-default-rtdb.firebaseio.com",
  projectId: "thevideocompanysitedb",
  storageBucket: "thevideocompanysitedb.firebasestorage.app",
  messagingSenderId: "513459974416",
  appId: "1:513459974416:web:258ecdcd71177d45d3b595",
  measurementId: "G-S9ML7XRF86",
};

// Initialize Firebase app
console.log('🔧 Initializing Firebase app...');

const managementApp = initializeApp(managementFirebaseConfig);
console.log('✅ Management app initialized:', managementApp.name);

// Export auth, database, Firestore, and Storage instances (same project)
export const auth = getAuth(managementApp);
export const managementAuth = getAuth(managementApp);
export const managementDb = getDatabase(managementApp);
export const db = getFirestore(managementApp);
export const storage = getStorage(managementApp);

// Initialize Analytics (only in browser environment)
if (typeof window !== 'undefined') {
  try {
    getAnalytics(managementApp);
    console.log('✅ Analytics initialized');
  } catch (error) {
    console.warn('⚠️ Analytics initialization failed:', error);
  }
}

console.log('✅ Firebase instances created');
console.log('🔍 Management database URL:', managementDb.app.options.databaseURL);
console.log('🔍 Management project ID:', managementDb.app.options.projectId);

// Function to initialize sample data (for testing)
export const initializeSampleData = async () => {
  try {
    console.log('🚀 Initializing sample data...');
    
    // Sample opening videos
    await set(ref(managementDb, 'home/opening-videos/sample1'), {
      title: 'Welcome to TVC',
      videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      description: 'Welcome video for The Video Company',
      isActive: true,
      order: 1,
      createdAt: Date.now()
    });

    // Sample featured videos
    await set(ref(managementDb, 'home/featured-videos/sample1'), {
      title: 'Featured Project',
      videoUrl: 'https://www.youtube.com/watch?v=0KoK0A3FMr4',
      description: 'A featured project showcase',
      isActive: true,
      order: 1,
      createdAt: Date.now()
    });

    console.log('✅ Sample data initialized successfully');
  } catch (error) {
    console.error('❌ Error initializing sample data:', error);
  }
};

// Function to initialize About page data (team and company info)
export const initializeAboutPageData = async () => {
  try {
    // Team members data
    const teamMembers = [
      {
        id: 1,
        name: 'Sarah Johnson',
        position: 'Chief Executive Officer',
        bio: `With over 15 years of industry experience, Sarah has led our company to new heights with her visionary leadership and strategic thinking. She began her career in marketing before transitioning to operations and eventually taking on the role of CEO. Under her guidance, the company has expanded into new markets and doubled its revenue. Sarah is known for her ability to inspire teams and create a culture of innovation and excellence. Her forward-thinking approach and commitment to sustainable business practices have positioned our company as an industry leader.`,
        skills: ['Leadership', 'Strategy'],
        color: '#1E5EAB',
        accent: '#3a7fcf',
        image: 'https://www.shutterstock.com/image-photo/portrait-handsome-man-black-suit-600nw-460050457.jpg',
      },
      {
        id: 2,
        name: 'Michael Chen',
        position: 'Chief Technology Officer',
        bio: `Michael's innovative approach to technology has revolutionized our product development process and kept us at the cutting edge of the industry. With a background in quantum computing and artificial intelligence, he brings a unique perspective to solving complex technical challenges. His leadership has resulted in multiple patents and award-winning products. Michael is passionate about creating technology that makes a positive impact on people's lives. He leads our R&D team with a focus on both short-term solutions and long-term technological advancement.`,
        skills: ['Innovation', 'Tech'],
        color: '#2a6fbd',
        accent: '#4b90e0',
        image: 'https://i.pinimg.com/736x/a2/95/0f/a2950faa53be5e2872037f1027fdfbe3.jpg',
      },
      {
        id: 3,
        name: 'Elena Rodriguez',
        position: 'Creative Director',
        bio: `Elena's creative vision has shaped our brand identity and helped us connect with audiences through compelling visual storytelling. Her background in fine arts and digital design gives her a versatile approach to creative challenges. She leads a team of designers who consistently produce award-winning work across multiple platforms. Elena believes that great design is both beautiful and functional, solving real problems while creating emotional connections. Her work has been recognized internationally and has helped establish our distinctive brand presence in a competitive market.`,
        skills: ['Design', 'Creativity'],
        color: '#174a87',
        accent: '#2a6fbd',
        image: 'https://media.istockphoto.com/id/493116226/photo/happy-smiling-businessman-in-suit.jpg?s=612x612&w=0&k=20&c=JSuvCrREfPOIxvXoyHWbVOl1LG7vT7TQzK6ZO203EuA=',
      },
      {
        id: 4,
        name: 'James Taylor',
        position: 'Operations Director',
        bio: `James has streamlined our operations and improved efficiency across all departments with his methodical approach and attention to detail. His background in supply chain management and process optimization has been invaluable in scaling our operations while maintaining quality and customer satisfaction. James is committed to sustainable business practices and has implemented eco-friendly initiatives throughout our supply chain. His leadership has been crucial in maintaining operational excellence during periods of rapid growth.`,
        skills: ['Operations', 'Management'],
        color: '#3a7fcf',
        accent: '#5b9fe0',
        image: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=facearea&w=600&h=900&q=80',
      },
      {
        id: 5,
        name: 'Aisha Patel',
        position: 'Marketing Director',
        bio: `Aisha's innovative marketing strategies have significantly expanded our market reach and strengthened our brand presence globally. Her expertise in digital marketing and consumer psychology has helped us connect with new audiences and build lasting customer relationships across diverse markets. Aisha is known for her data-driven approach combined with creative storytelling. She has pioneered several successful campaigns that have set new benchmarks in the industry and significantly contributed to our company's growth.`,
        skills: ['Marketing', 'Strategy'],
        color: '#2a6fbd',
        accent: '#4b90e0',
        image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=facearea&w=600&h=900&q=80',
      },
      {
        id: 6,
        name: 'Robert Kim',
        position: 'Financial Analyst',
        bio: `Robert's financial expertise has been instrumental in optimizing our resource allocation and ensuring sustainable growth. His analytical skills and strategic thinking have helped us navigate complex financial challenges and identify new opportunities for investment and expansion. Robert combines traditional financial acumen with innovative approaches to funding and investment. His forecasting models have proven remarkably accurate, allowing the company to make confident decisions even in uncertain market conditions.`,
        skills: ['Finance', 'Analysis'],
        color: '#174a87',
        accent: '#2a6fbd',
        image: 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?auto=format&fit=facearea&w=600&h=900&q=80',
      },
    ];

    // Company info (example, update as needed)
    const companyInfo = {
      name: 'The Video Company',
      founded: 2010,
      description: 'Founded in 2010, our company has grown from a small startup to an industry leader. We believe in pushing boundaries and challenging the status quo to deliver exceptional results for our clients.',
      mission: 'Deliver exceptional results for our clients through innovation and excellence.',
      vision: 'To be the leading provider of creative and innovative solutions in our industry.',
      logo: 'logoBlue', // Update with actual logo path or URL if needed
    };

    // Push team members
    await set(ref(managementDb, 'about/team'), teamMembers);
    // Push company info
    await set(ref(managementDb, 'about/company'), companyInfo);

    console.log('✅ About page data initialized successfully');
  } catch (error) {
    console.error('❌ Error initializing About page data:', error);
  }
};

// Create default export object
const firebaseConfig = { managementApp };

export default firebaseConfig;